import { useQuery } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import { formatScore, isWeekly, plainLabel, sourceLink } from './scoreboard';
import type { ScoreCell, ScoreMetric, ScoreSection } from './scoreboard';
import { momentum } from './scoreboardDashboard';
import { buildDetails, itemTableNames, splitItemLabel, type DetailRow } from './scoreboardDetails';
import {
  addDays,
  dayList,
  indexDays,
  sparkPoints,
  variantOf,
  variantTags,
  windowChange,
  windowTotal,
  type DayIndex,
} from './scoreboardSeries';
import type { TrendPayload } from './trends';

/** Reads the raw days of one section from a start day. */
export type DaysReader = (section: string, since: string) => Promise<TrendPayload>;

type Range = 1 | 7 | 30;

export function Source({ url, compact = false }: { url: string | null; compact?: boolean }) {
  const link = sourceLink(url);
  if (!link) return null;
  return compact ? (
    <a className="score-source" href={link} target="_blank" rel="noopener noreferrer">
      <span aria-hidden="true">↗</span>
      <span className="ui-visually-hidden">Open source (new tab)</span>
    </a>
  ) : (
    <a href={link} target="_blank" rel="noopener noreferrer">
      Open source<span className="ui-visually-hidden"> (new tab)</span>
    </a>
  );
}

function Segmented<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: [T, string][];
  onChange: (next: T) => void;
}) {
  return (
    <div className="sn-seg" role="group" aria-label={label}>
      {options.map(([key, text]) => (
        <button key={key} type="button" aria-pressed={key === value} onClick={() => onChange(key)}>
          {text}
        </button>
      ))}
    </div>
  );
}

/** A small line of the last days. Facts only, so no good or bad colour. */
function Spark({ points }: { points: string | null }) {
  return (
    <svg
      className={points ? 'sn-spark' : 'sn-spark sn-spark--empty'}
      viewBox="0 0 64 20"
      aria-hidden="true"
      focusable="false"
    >
      <polyline points={points ?? '2,10 62,10'} />
    </svg>
  );
}

const pct = (change: number) => {
  const flat = Math.abs(change) < 0.005;
  const arrow = flat ? '→' : change > 0 ? '▲' : '▼';
  return `${arrow} ${Math.abs(change * 100).toFixed(0)}%`;
};

export function SectionNumbers({
  section,
  date,
  yesterday,
  cacheScope = 'local-qa',
  days,
}: {
  section: ScoreSection;
  /** The day the Board is showing. */
  date: string;
  yesterday: boolean;
  cacheScope?: string;
  /** Raw days. Without it the card shows the totals the Board already has. */
  days?: DaysReader;
}) {
  const details = buildDetails(section);
  const snapshot = section.kind === 'snapshot';
  const dayOfMonth = Number(date.slice(8));
  const [range, setRange] = useState<Range>(7);
  const [variant, setVariant] = useState('all');
  const [openRow, setOpenRow] = useState<string | null>(null);
  const baseId = useId();

  // One small read, and only while this card is open. No polling, no refetch on focus.
  const series = useQuery({
    queryKey: ['marketing-score-days', cacheScope, section.id, date],
    queryFn: () => (days as DaysReader)(section.id, addDays(date, -59)),
    enabled: days !== undefined && !snapshot,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const index: DayIndex | null = useMemo(
    () => (series.data ? indexDays(series.data) : null),
    [series.data],
  );
  const failed = series.isError;
  const ranged = days !== undefined && !snapshot;
  const active: Range = ranged ? range : 7;
  const dayName = yesterday ? 'Yesterday' : 'Selected day';
  const windowName = active === 1 ? dayName : `Last ${active} days`;
  const needsData = active === 30 && index === null;
  const pending = failed ? 'Unavailable' : 'Loading…';

  const cellFor = (metric: ScoreMetric): ScoreCell | undefined => {
    if (snapshot || active === 1) return metric.totals.selected;
    if (active === 7) return metric.totals.seven;
    return index ? windowTotal(metric, index, dayList(date, 30)) : undefined;
  };
  const changeFor = (metric: ScoreMetric): number | null => {
    if (active === 7) return momentum(metric);
    if (!index) return null;
    return windowChange(metric, index, date, active, active === 1 ? 1 : 10);
  };
  const pointsFor = (metric: ScoreMetric): string | null =>
    index ? sparkPoints(metric, index, date, active === 30 ? 30 : 14, 64, 20) : null;
  const value = (metric: ScoreMetric) =>
    needsData && !snapshot ? pending : formatScore(cellFor(metric)?.value, metric);

  const changeText = (metric: ScoreMetric): string => {
    if (snapshot)
      return metric.change === null
        ? ''
        : `${metric.change > 0 ? '+' : ''}${formatScore(metric.change, metric)} vs 7 days earlier`;
    const change = changeFor(metric);
    if (change !== null) return pct(change);
    if (active === 7) return 'Needs 5 days';
    return index ? 'Needs more days' : pending;
  };

  const detailFor = (row: DetailRow): [string, string][] => {
    const metric = row.metric;
    if (snapshot) {
      return [
        ['7 days earlier', formatScore(metric.totals.prior?.value, metric)],
        [
          'Change',
          metric.change === null
            ? 'Not available'
            : `${metric.change > 0 ? '+' : ''}${formatScore(metric.change, metric)}`,
        ],
      ];
    }
    const cell = cellFor(metric);
    const lines: [string, string][] = [];
    if (active !== 1) {
      lines.push([dayName, formatScore(metric.totals.selected?.value, metric)]);
      if (!needsData)
        lines.push([
          windowName,
          `${cell?.entered_days ?? 0} of ${active} ${metric.value_type === 'rate' ? 'paired days' : 'days entered'}`,
        ]);
    }
    for (const rate of row.rates) {
      const rc = cellFor(rate);
      lines.push([
        rate.label,
        needsData
          ? pending
          : `${formatScore(rc?.value, rate)}${active > 1 ? ` · ${rc?.entered_days ?? 0} of ${active} paired days` : ''}`,
      ]);
    }
    lines.push([
      'Month to date',
      `${formatScore(metric.totals.month?.value, metric)} · ${metric.totals.month?.entered_days ?? 0} of ${dayOfMonth} days`,
    ]);
    if (metric.target !== null) lines.push(['Target', formatScore(metric.target, metric)]);
    return lines;
  };

  const rowFor = (row: DetailRow) => {
    const metric = row.metric;
    const open = openRow === metric.id;
    const panel = `${baseId}-${metric.id}`;
    const cell = cellFor(metric);
    const muted = needsData || cell?.value == null;
    return (
      <li key={metric.id} className={open ? 'sn-row is-open' : 'sn-row'}>
        <button
          type="button"
          className="sn-row-main"
          aria-expanded={open}
          aria-controls={panel}
          onClick={() => setOpenRow(open ? null : metric.id)}
        >
          <span className="sn-name">
            <span>
              {plainLabel(metric.label)}
              {isWeekly(metric.label) ? <em className="sn-tag">weekly</em> : null}
            </span>
            {row.rates.map((rate) => (
              <small key={rate.id}>
                <span>{needsData ? pending : formatScore(cellFor(rate)?.value, rate)}</span>{' '}
                {rate.label}
              </small>
            ))}
            {metric.value_type === 'rate' ? <small>Calculated for you</small> : null}
          </span>
          {snapshot ? null : <Spark points={pointsFor(metric)} />}
          <span className="sn-num">
            <strong className={muted ? 'sn-muted' : undefined}>{value(metric)}</strong>
            <small>{changeText(metric)}</small>
          </span>
          <span className="sn-chev" aria-hidden="true">
            ›
          </span>
        </button>
        {open ? (
          <div id={panel} className="sn-detail">
            <dl>
              {detailFor(row).map(([label, text]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{text}</dd>
                </div>
              ))}
            </dl>
            <Source url={metric.source_url} />
          </div>
        ) : null}
      </li>
    );
  };

  // The stage tiles: the first number of each stage, only when there are stages to compare.
  const tiles = details.groups
    .filter((group) => group.title && group.rows[0])
    .map((group) => ({ title: group.title ?? '', metric: group.rows[0]!.metric }));
  const entered = Math.max(
    0,
    ...details.groups.flatMap((group) =>
      group.rows.map((row) => cellFor(row.metric)?.entered_days ?? 0),
    ),
  );
  const tables = details.itemTables;
  const tags = variantTags(tables.flatMap((table) => table.rows.map((row) => row.name)));
  const keep = (name: string) => variant === 'all' || variantOf(name) === variant;
  const dash = (label: string) => (
    <span className="sn-muted">
      <span aria-hidden="true">—</span>
      <span className="ui-visually-hidden">{label}</span>
    </span>
  );

  const renderTable = (table: (typeof tables)[number], tableIndex: number) => {
    const names = itemTableNames(table.columns);
    return (
      <div key={table.columns.map((c) => c.name).join('|')} className="sn-item-block">
        <div className="sn-bar">
          <h4 className="sn-group-title">
            {names.title} · {windowName.toLowerCase()}
          </h4>
          {tableIndex === 0 && tags.length > 0 ? (
            <Segmented<string>
              label="Variant"
              value={variant}
              options={[['all', 'All'], ...tags.map((tag): [string, string] => [tag, tag])]}
              onChange={setVariant}
            />
          ) : null}
        </div>
        <div
          className="score-table-scroll sn-card"
          role="region"
          aria-label={`${section.name} item numbers${tableIndex ? `, table ${tableIndex + 1}` : ''}`}
          tabIndex={0}
        >
          <table className="sn-item-table">
            <thead>
              <tr>
                <th scope="col">{names.head}</th>
                {table.columns.map((column) => (
                  <th scope="col" key={column.name}>
                    {column.name}
                    {column.weekly ? <em className="sn-tag">weekly</em> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows
                .filter((row) => keep(row.name))
                .map((row) => (
                  <tr key={row.name}>
                    <th scope="row">{row.name}</th>
                    {table.columns.map((column) => {
                      const cell = row.cells[column.name];
                      if (!cell) return <td key={column.name}>{dash('Not tracked')}</td>;
                      if (needsData && !snapshot)
                        return (
                          <td key={column.name} className="sn-muted">
                            {pending}
                          </td>
                        );
                      const cellValue = cellFor(cell.field)?.value;
                      return (
                        <td key={column.name}>
                          {cellValue == null
                            ? dash(formatScore(cellValue, cell.field))
                            : formatScore(cellValue, cell.field)}
                          {cell.rates.map((rate) => {
                            const rateValue = cellFor(rate)?.value;
                            return rateValue == null ? null : (
                              <small key={rate.id} className="sn-cell-rate">
                                {rate.rate_format === 'percent'
                                  ? `${Math.round(rateValue * 100)}%`
                                  : formatScore(rateValue, rate)}
                                <span className="ui-visually-hidden">
                                  {' '}
                                  {splitItemLabel(rate.label)?.metric ?? rate.label}
                                </span>
                              </small>
                            );
                          })}
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  const hasList = details.groups.some((group) => group.rows.length > 0);
  // Narrow tables (one or two numbers, like pages) sit beside the notes instead of running full width.
  const wide = tables.filter((table) => table.columns.length > 2);
  const narrow = tables.filter((table) => table.columns.length <= 2);
  const notes = (
    <p className="sn-notes">
      {tables.some((t) =>
        t.rows.some((r) => Object.values(r.cells).some((c) => c?.rates.length)),
      ) ? (
        <>
          Small grey % = rate worked out by the board
          {section.slug === 'vsl-site' ? ': watched ÷ plays, applications ÷ clicks' : ''}.
          <br />
        </>
      ) : null}
      {tables.some((t) => t.columns.some((c) => c.weekly)) ? (
        <>
          Weekly columns show the week entered; daily columns add up the {windowName.toLowerCase()}.
          <br />
        </>
      ) : null}
      Blank is unknown, not zero.
    </p>
  );

  return (
    <div className="sn">
      {ranged ? (
        <div className="sn-bar">
          <Segmented<Range>
            label="Range"
            value={active}
            options={[
              [1, dayName],
              [7, '7 days'],
              [30, '30 days'],
            ]}
            onChange={setRange}
          />
          {failed ? (
            <small className="sn-muted">Daily lines and 30 days are unavailable right now.</small>
          ) : null}
        </div>
      ) : null}
      {hasList && tiles.length >= 2 ? (
        <div className="sn-tiles">
          {tiles.map((tile) => (
            <div key={tile.title} className="sn-tile">
              <span>{tile.title}</span>
              <strong>{value(tile.metric)}</strong>
            </div>
          ))}
        </div>
      ) : null}
      {hasList ? (
        <>
          <div className="sn-groups" role="region" aria-label={`${section.name} numbers`}>
            {details.groups.map((group, groupIndex) => (
              <div key={`${group.title ?? 'all'}-${groupIndex}`} className="sn-group">
                {group.title ? <h4 className="sn-group-title">{group.title}</h4> : null}
                <ul className="sn-list">{group.rows.map(rowFor)}</ul>
              </div>
            ))}
          </div>
          <p className="sn-foot">
            {snapshot || active === 1 || needsData
              ? ''
              : `Based on ${entered} of ${active} days entered. `}
            Blank is unknown, not zero. Open a row for more.
          </p>
        </>
      ) : null}
      {tables.length ? (
        <section className="sn-items" aria-label={`${section.name} by item`}>
          {wide.map((table) => renderTable(table, tables.indexOf(table)))}
          {narrow.length ? (
            <div className="sn-side">
              <div className="sn-side-tables">
                {narrow.map((table) => renderTable(table, tables.indexOf(table)))}
              </div>
              {notes}
            </div>
          ) : (
            notes
          )}
        </section>
      ) : null}
    </div>
  );
}
